// src/components/subscription/SubscriptionManager.jsx - サブスクリプション管理コンポーネント

import React, { useState, useEffect } from 'react';
import { buttonStyles } from '../../styles/commonStyles';

const styles = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem'
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '1rem',
    maxWidth: '600px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    position: 'relative'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1.5rem 1.5rem 1rem',
    borderBottom: '1px solid #f3f4f6'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    color: '#6b7280',
    cursor: 'pointer',
    padding: '0.25rem',
    lineHeight: 1
  },
  title: {
    margin: 0,
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#1f2937'
  },
  content: {
    padding: '1rem 1.5rem 1.5rem'
  },
  planSection: {
    textAlign: 'center',
    marginBottom: '1.5rem'
  },
  planIcon: {
    fontSize: '3rem',
    marginBottom: '1rem'
  },
  planTitle: {
    margin: '0 0 0.5rem',
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#1f2937'
  },
  button: {
    width: '100%',
    padding: '0.75rem 1.5rem',
    borderRadius: '0.5rem',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '1rem',
    marginBottom: '0.5rem'
  },
  buttonPrimary: buttonStyles.primary,
  buttonDanger: buttonStyles.danger,
  buttonSecondary: buttonStyles.secondary
};

const SubscriptionManager = ({ userId, onPlanChange, onClose }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (userId) {
      fetchSubscriptionStatus();
    }
  }, [userId]);

  const fetchSubscriptionStatus = async () => {
    try {
      setLoading(true);
      setError('');

      const userPlan = localStorage.getItem('userPlan') || 'free';

      if (userPlan === 'free') {
        setSubscription({ plan: 'free', subscription: null });
        setLoading(false);
        return;
      }

      const subscriptionData = {
        plan: 'premium',
        subscription: {
          id: localStorage.getItem('stripeSessionId') || localStorage.getItem('checkoutSessionId'),
          status: 'active',
          current_period_start: Math.floor(new Date(localStorage.getItem('premiumActivatedAt') || Date.now()).getTime() / 1000),
          current_period_end: Math.floor((new Date(localStorage.getItem('premiumActivatedAt') || Date.now()).getTime() + 30 * 24 * 60 * 60 * 1000) / 1000),
          cancel_at_period_end: false,
          canceled_at: null,
          plan: {
            amount: 980,
            currency: 'jpy',
            interval: 'month'
          }
        }
      };

      setSubscription(subscriptionData);

    } catch (error) {
      console.error('Fetch subscription status error:', error);
      setError('サブスクリプション情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = () => {
    if (window.upgradeToPremium) {
      window.upgradeToPremium();
    } else {
      alert('アップグレード機能を初期化中です。少し待ってからもう一度お試しください。');
    }
    onClose();
  };

  const handleCancel = async (cancelType = 'at_period_end') => {
    try {
      setActionLoading(true);
      setError('');
      setSuccess('');

      if (cancelType === 'immediately') {
        const keysToRemove = [
          'userPlan', 'user_plan', 'plan', 'subscriptionStatus',
          'premiumActivatedAt', 'stripeSessionId', 'checkoutSessionId',
          'authToken', 'premiumToken'
        ];

        keysToRemove.forEach(key => {
          localStorage.removeItem(key);
        });

        localStorage.setItem('userPlan', 'free');
        setSuccess('プレミアムプランを解約しました。無料プランに戻りました。');
        if (onPlanChange) onPlanChange('free');

      } else {
        localStorage.setItem('cancelAtPeriodEnd', 'true');
        localStorage.setItem('cancelScheduledAt', new Date().toISOString());
        setSuccess('期間終了時に解約予定として設定されました。');
      }

      setShowCancelConfirm(false);
      await fetchSubscriptionStatus();

    } catch (error) {
      console.error('Cancel subscription error:', error);
      setError('解約処理でエラーが発生しました');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.modal}>
          <div style={styles.header}>
            <h3 style={styles.title}>読み込み中...</h3>
            <button onClick={onClose} style={styles.closeButton}>×</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h3 style={styles.title}>📄 アカウント設定</h3>
          <button onClick={onClose} style={styles.closeButton}>×</button>
        </div>

        <div style={styles.content}>
          {subscription?.plan === 'free' ? (
            <div>
              <div style={styles.planSection}>
                <div style={styles.planIcon}>🆓</div>
                <h4 style={styles.planTitle}>無料プランをご利用中</h4>
                <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
                  1日3回まで高品質AI投稿生成をお楽しみいただけます。
                </p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <h5 style={{ color: '#fbbf24', marginBottom: '1rem', textAlign: 'center' }}>
                  🚀 プレミアムプランの特典
                </h5>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  <div style={{ marginBottom: '0.5rem' }}>⚡ 無制限AI投稿生成</div>
                  <div style={{ marginBottom: '0.5rem' }}>𝕏 (旧Twitter)自動投稿</div>
                  <div style={{ marginBottom: '0.5rem' }}>@ Threads自動投稿</div>
                  <div style={{ marginBottom: '0.5rem' }}>🔄 同時投稿機能</div>
                  <div style={{ marginBottom: '0.5rem' }}>👑 広告なし</div>
                </div>
              </div>

              <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1f2937' }}>¥980</div>
                <div style={{ color: '#6b7280' }}>月額（税込）</div>
              </div>

              <button
                onClick={handleUpgrade}
                style={{ ...styles.button, ...styles.buttonPrimary }}
              >
                👑 プレミアムプランにアップグレード
              </button>
            </div>
          ) : (
            <div>
              <div style={styles.planSection}>
                <div style={styles.planIcon}>👑</div>
                <h4 style={styles.planTitle}>プレミアムプラン</h4>
                <p style={{ color: '#10b981', fontWeight: 'bold' }}>アクティブ</p>
              </div>

              {error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '0.75rem', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.875rem' }}>
                  {error}
                </div>
              )}

              {success && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#16a34a', padding: '0.75rem', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.875rem' }}>
                  {success}
                </div>
              )}

              <div style={{ marginBottom: '1.5rem' }}>
                <h5 style={{ marginBottom: '0.5rem', fontWeight: 600 }}>📄 契約情報</h5>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  <div>料金: ¥980/月</div>
                  <div>開始日: {formatDate(subscription.subscription?.current_period_start)}</div>
                  <div>次回更新: {formatDate(subscription.subscription?.current_period_end)}</div>
                </div>
              </div>

              <button
                onClick={() => setShowCancelConfirm(true)}
                style={{ ...styles.button, ...styles.buttonDanger }}
                disabled={actionLoading}
              >
                {actionLoading ? '処理中...' : 'プランを解約'}
              </button>
            </div>
          )}

          {/* 解約確認モーダル */}
          {showCancelConfirm && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1001
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '0.5rem',
                padding: '1.5rem',
                maxWidth: '400px',
                margin: '1rem'
              }}>
                <h4 style={{ marginBottom: '1rem' }}>解約の確認</h4>
                <p style={{ marginBottom: '1.5rem', color: '#6b7280' }}>
                  解約のタイミングを選択してください。
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <button
                    onClick={() => handleCancel('at_period_end')}
                    style={{ ...styles.button, ...styles.buttonSecondary }}
                  >
                    期間終了時に解約（推奨）
                  </button>
                  <button
                    onClick={() => handleCancel('immediately')}
                    style={{ ...styles.button, ...styles.buttonDanger }}
                  >
                    今すぐ解約
                  </button>
                  <button
                    onClick={() => setShowCancelConfirm(false)}
                    style={{ ...styles.button, background: '#f3f4f6', color: '#6b7280' }}
                  >
                    キャンセル
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManager;
